import qs from 'qs';
import { UserType, type UsersFeaturesResponse, type UsersResponse } from '@shared/api/rest/users';
import { AccountType, type AccountResponse } from '@shared/api/rest/accounts';
import DashboardIcon from '@assets/icons/dashboard.svg';
import AccountIcon from '@assets/icons/account.svg';
import PaymentIcon from '@assets/icons/payment.svg';
import CardIcon from '@assets/icons/card.svg';
import UsersIcon from '@assets/icons/users.svg';
import ReportIcon from '@assets/icons/report.svg';
import StatementIcon from '@assets/icons/statement.svg';
import IntegrationIcon from '@assets/icons/integration.svg';
import ChatIcon from '@assets/icons/chat.svg';

export class RoutePaths {
  static accounts = {
    path: 'accounts',
    onNavigate: () => '/accounts',
  };
  static account = {
    path: 'accounts/:id',
    onNavigate: (id: number) => `/accounts/${id}`,
  };
  static accountTransaction = {
    path: 'accounts/transactions/:tranId',
    onNavigate: (tranId: string) => `/accounts/transactions/${tranId}`,
  };

  static alerts = {
    path: 'alerts',
    onNavigate: (query?: { id?: string }) => `/alerts${qs.stringify(query, { addQueryPrefix: true })}`,
  };

  static callback = {
    path: 'callback',
    onNavigate: () => '/callback',
  };

  static cards = {
    path: 'cards',
    onNavigate: () => '/cards',
  };
  static card = {
    path: 'cards/:id',
    onNavigate: (id: number) => `/cards/${id}`,
  };

  static dashboard = {
    path: '',
    onNavigate: () => '/',
  };

  static error = {
    path: 'error',
    onNavigate: () => '/error',
  };

  static integrations = {
    path: 'integrations',
    onNavigate: () => '/integrations',
  };
  static integration = {
    path: 'integrations/:id',
    onNavigate: (id: string) => `/integrations/${id}`,
  };

  static messages = {
    path: 'message-centre',
    onNavigate: () => '/message-centre',
  };

  static payments = {
    path: 'payments',
    onNavigate: () => '/payments',
  };
  static paymentsInsignia = {
    path: 'payments/insignia',
    onNavigate: () => '/payments/insignia',
  };
  static paymentsOther = {
    path: 'payments/other',
    onNavigate: () => '/payments/other',
  };
  static paymentsCard = {
    path: 'payments/card',
    onNavigate: () => '/payments/card',
  };
  static paymentsRecipient = {
    path: 'payments/recipient/:id',
    onNavigate: (id: string) => `/payments/recipient/${id}`,
  };
  static paymentRecipients = {
    path: 'recipients',
    onNavigate: () => '/payments/recipients',
  };
  static paymentNewRecipients = {
    path: 'new',
    onNavigate: () => '/payments/recipients/new',
  };
  static paymentRecipient = {
    path: ':recipientId',
    onNavigate: (recipientId: string) => `/payments/recipients/${recipientId}`,
  };

  static profile = {
    path: 'profile',
    onNavigate: () => '/profile',
  };

  static reports = {
    path: 'reports',
    onNavigate: () => '/reports',
  };

  static signup = {
    path: 'signup',
    onNavigate: () => '/signup',
  };

  static success = {
    path: 'success',
    onNavigate: () => '/success',
  };

  static statements = {
    path: 'statements',
    onNavigate: () => '/statements',
  };

  static users = {
    path: 'user-management',
    onNavigate: (query?: { successDeleted?: boolean }) => `/user-management${qs.stringify(query, { addQueryPrefix: true })}`,
  };
  static user = {
    path: 'user-management/:id',
    onNavigate: (id: string) => `/user-management/${id}`,
  };
}

const MENU = [
  { name: 'Dashboard', link: RoutePaths.dashboard.onNavigate(), Icon: <DashboardIcon /> },
  { name: 'Accounts', link: RoutePaths.accounts.onNavigate(), Icon: <AccountIcon /> },
  { name: 'Payments', link: RoutePaths.payments.onNavigate(), Icon: <PaymentIcon /> },
  { name: 'Cards', link: RoutePaths.cards.onNavigate(), Icon: <CardIcon /> },
  { name: 'User management', link: RoutePaths.users.onNavigate(), Icon: <UsersIcon /> },
  { name: 'Reports', link: RoutePaths.reports.onNavigate(), Icon: <ReportIcon /> },
  { name: 'Statements', link: RoutePaths.statements.onNavigate(), Icon: <StatementIcon /> },
  { name: 'Integrations', link: RoutePaths.integrations.onNavigate(), Icon: <IntegrationIcon /> },
  { name: 'Message centre', link: RoutePaths.messages.onNavigate(), Icon: <ChatIcon /> },
];

export class RouteUtils {
  static getAllowedMenuItems(props: { profile: UsersResponse | null; features: UsersFeaturesResponse | null; accounts: AccountResponse[] }) {
    const isShowMenuItem = (link: string) => {
      switch (link) {
        case RoutePaths.messages.onNavigate():
          return !!props.features?.messages?.viewOrReply;
        case RoutePaths.statements.onNavigate():
          return !!(
            props.features?.statements?.viewOrDownloadAccountStatement ||
            props.features?.statements?.viewOrDownloadCardStatement ||
            props.features?.statements?.viewOrDownloadConsolidatedStatement
          );
        case RoutePaths.users.onNavigate():
          return !!props.features?.userManagement?.viewUsers;
        case RoutePaths.reports.onNavigate():
          return !!props.features?.reports?.viewReportsSection;
        case RoutePaths.accounts.onNavigate():
          return !!props.features?.accountFlows?.length;
        case RoutePaths.integrations.onNavigate():
          return props.profile?.userType === UserType.ADMIN;
        case RoutePaths.payments.onNavigate():
          return props.accounts?.some((item: any) => item.type === AccountType.Payment);
        case RoutePaths.cards.onNavigate():
          return !!props.features?.cardFlows?.length;
        default:
          return true;
      }
    };

    return MENU.filter((item) => isShowMenuItem(item.link));
  }
}
